import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { AuthModal } from "../components/AuthModal";
import { SpeedTest } from "../components/SpeedTest";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { ShieldCheck, Lock, Zap, Users, ArrowRight, Wifi, Plane, BadgeCheck, Globe, Star, Trophy } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }) };

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signup");
  const [leaders, setLeaders] = useState([]);
  useEffect(() => { api.get("/leaderboard").then((r) => setLeaders(r.data)).catch(() => {}); }, []);

  const openAuth = (mode = "signup") => { setAuthMode(mode); setAuthOpen(true); };
  const goHost = () => (user ? navigate(user.role === "host" ? "/dashboard" : "/plans") : openAuth("signup"));

  return (
    <div className="min-h-screen bg-background">
      <Navbar onRequireAuth={() => openAuth("signin")} />

      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="pointer-events-none absolute -top-24 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-foreground/5 blur-3xl" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-7">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Verified Peer-to-Peer Internet</span>
            </motion.div>

            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]">
              Share Wi-Fi.<br />Trust your<br /><span className="text-muted-foreground">neighbors.</span>
            </motion.h1>

            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="mt-6 max-w-md text-lg text-muted-foreground leading-relaxed">
              OPNWiFi connects ID-verified travelers and neighbors to trusted home hotspots. Affordable, encrypted, and ready in seconds.
            </motion.p>

            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-9 flex flex-wrap gap-3">
              <button onClick={() => navigate("/search")} data-testid="hero-find-wifi"
                className="group h-12 px-6 rounded-full bg-foreground text-background text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity">
                Find Nearby Wi-Fi <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button onClick={goHost} data-testid="hero-host-wifi"
                className="h-12 px-6 rounded-full border border-border text-sm font-medium flex items-center gap-2 hover:bg-accent transition-colors">
                <Wifi size={16} /> Host a Hotspot
              </button>
            </motion.div>

            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={4} className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2"><BadgeCheck size={16} className="text-emerald-500" /> 100% ID-verified</div>
              <div className="flex items-center gap-2"><Lock size={16} /> Bank-level encryption</div>
            </motion.div>
          </div>

          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="relative">
            <div className="absolute inset-0 grid place-items-center">
              <div className="radar-ring absolute h-56 w-56 rounded-full border border-foreground/20" />
              <div className="radar-ring absolute h-56 w-56 rounded-full border border-foreground/20" style={{ animationDelay: "1.5s" }} />
            </div>
            <div className="relative"><SpeedTest maxSpeed={940} /></div>
          </motion.div>
        </div>
      </section>

      {/* Value bento */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3">Why OPNWiFi</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-xl mb-12">Trust built into every connection.</h2>
        <div className="grid gap-4 md:grid-cols-3 md:grid-rows-2">
          {[
            { icon: ShieldCheck, title: "ID-verified members", body: "Every host and guest passes government ID + selfie verification before they connect. No anonymous strangers.", big: true },
            { icon: Lock, title: "Encrypted sessions", body: "Credentials are encrypted end-to-end and rotated per session." },
            { icon: Zap, title: "Zero setup fee", body: "List your router in minutes. No hardware, no contracts." },
            { icon: Users, title: "Neighborly earnings", body: "Hosts keep 90% and earn $50–$200+/mo sharing idle bandwidth.", big: true },
            { icon: Globe, title: "Anywhere you roam", body: "From Brooklyn to Memphis, find fast Wi-Fi on the go." },
          ].map((f, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}
              className={`rounded-2xl border border-border bg-card p-7 hover:border-zinc-400 dark:hover:border-zinc-600 transition-all ${f.big ? "md:row-span-1 md:col-span-2" : ""}`}>
              <f.icon size={22} className="mb-4" />
              <h3 className="font-display text-lg font-medium mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-border bg-card/30">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid gap-10 md:grid-cols-3">
          {[
            { n: "01", icon: Plane, t: "Verify your identity", d: "Upload your ID and a selfie. Get your verified badge after review." },
            { n: "02", icon: Wifi, t: "Find or host a hotspot", d: "Browse nearby verified hotspots, or list your own router and set a price." },
            { n: "03", icon: Zap, t: "Connect instantly", d: "With an active membership, get encrypted credentials and get online in seconds." },
          ].map((s, i) => (
            <div key={i} className="relative">
              <span className="font-mono text-xs text-muted-foreground">{s.n}</span>
              <s.icon size={24} className="my-4" />
              <h3 className="font-display text-xl font-medium mb-2">{s.t}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing preview */}
      <section id="pricing" className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3">Membership</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">Simple, honest pricing.</h2>
          </div>
          <button onClick={() => navigate("/plans")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="view-all-plans">View all plans <ArrowRight size={14} /></button>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { name: "Traveler Day Pass", price: "$4.99", unit: "/day", features: ["24-hour access", "Any verified hotspot", "Encrypted connection"] },
            { name: "Neighbor Monthly", price: "$19.99", unit: "/mo", features: ["Unlimited connections", "Priority hotspots", "Cancel anytime"], popular: true },
            { name: "Host Pro", price: "$9.99", unit: "/mo", features: ["Keep 90% of earnings", "Unlimited listings", "Payout dashboard"] },
          ].map((p, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className={`rounded-2xl border p-7 flex flex-col ${p.popular ? "border-foreground bg-card" : "border-border bg-card/50"}`}>
              {p.popular && <span className="self-start font-mono text-xs uppercase tracking-wider px-2 py-1 rounded-full bg-foreground text-background mb-4">Most popular</span>}
              <h3 className="font-display text-lg font-medium">{p.name}</h3>
              <div className="mt-3 mb-5 flex items-baseline gap-1"><span className="font-display text-3xl font-bold">{p.price}</span><span className="text-sm text-muted-foreground">{p.unit}</span></div>
              <ul className="space-y-2 mb-6 flex-1">{p.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground"><BadgeCheck size={14} className="text-emerald-500" /> {f}</li>)}</ul>
              <button onClick={() => navigate("/plans")} className={`h-11 rounded-xl text-sm font-medium transition-opacity hover:opacity-90 ${p.popular ? "bg-foreground text-background" : "border border-border"}`}>Choose plan</button>
            </motion.div>
          ))}
        </div>
      </section>

      {leaders.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20" data-testid="leaderboard-section">
          <div className="flex items-center gap-2 mb-3"><Trophy size={16} /><p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Top-Rated Hosts</p></div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-xl mb-12">The neighbors everyone trusts.</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {leaders.map((h, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}
                className="rounded-2xl border border-border bg-card p-6 flex items-center gap-4 hover:border-zinc-400 dark:hover:border-zinc-600 transition-all">
                <div className="h-12 w-12 rounded-full bg-foreground text-background grid place-items-center font-display font-bold shrink-0">{(h.host_name || "H")[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5"><p className="font-medium truncate">{h.host_name}</p>{h.host_verified && <BadgeCheck size={14} className="text-emerald-500 shrink-0" />}</div>
                  <p className="text-xs text-muted-foreground truncate">{h.cities.join(" · ") || "OPNWiFi host"}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="flex items-center gap-1 font-medium"><Star size={14} className="fill-amber-400 text-amber-400" /> {h.rating}</div>
                  <p className="text-xs text-muted-foreground">{h.reviews} review{h.reviews === 1 ? "" : "s"}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Founder */}
      <section className="border-t border-border bg-card/30">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2">
            <div className="aspect-square max-w-sm rounded-2xl border border-border bg-background grid place-items-center overflow-hidden">
              <div className="text-center p-8">
                <div className="mx-auto mb-5 h-20 w-20 rounded-full bg-foreground text-background grid place-items-center font-display text-2xl font-bold">CF</div>
                <Logo size={40} className="mx-auto text-foreground mb-4" />
                <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Brooklyn, NY → Memphis, TN</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-3">
            <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3">The Founder</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight mb-5">Christopher M. Ferrer</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              A real estate and IT professional originally from Brooklyn, New York, now residing in Memphis, Tennessee, Christopher is always thinking of ideas and technology to make people's lives more affordable, secure, and easy.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              OPNWiFi was born from a simple belief: reliable internet shouldn't be a luxury. By letting verified neighbors safely share what they already have, Christopher is building a more connected — and more trusting — community.
            </p>
          </div>
        </div>
      </section>

      <Footer />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode={authMode} />
    </div>
  );
}
